import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useNavigate, useSearchParams, useLocation } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AnalyticsSummary, SavedView, Team, Usage } from "@autopilot/shared";
import { api, ws } from "../lib/api";
import { useWorkspace, meKey } from "../lib/session";
import { useRealtime, useRealtimeEvent } from "../lib/realtime";
import { Icon, Logo, type IconName } from "../ui/Icon";
import { Avatar, Badge, Button, Kbd, Menu, Tooltip, useToast } from "../ui/kit";
import { CommandPalette } from "./CommandPalette";

export interface ConversationCounts {
  all: number;
  mine: number;
  unassigned: number;
  aiHandled: number;
  waiting: number;
  resolved: number;
  views: Record<string, number>;
  teams: Record<string, number>;
}

export const countsKey = (wid: string) => ["counts", wid] as const;

/** Inbox folder ids used in `?folder=` on the inbox route. */
export const INBOX_FOLDERS: { id: string; label: string; icon: IconName; count: keyof Omit<ConversationCounts, "views" | "teams"> }[] = [
  { id: "all", label: "All conversations", icon: "inbox", count: "all" },
  { id: "mine", label: "Assigned to me", icon: "mine", count: "mine" },
  { id: "unassigned", label: "Unassigned", icon: "unassigned", count: "unassigned" },
  { id: "ai", label: "Ember handled", icon: "ai", count: "aiHandled" },
  { id: "waiting", label: "Waiting", icon: "clock", count: "waiting" },
  { id: "resolved", label: "Resolved", icon: "check", count: "resolved" },
];

const WORKSPACE_NAV: { to: string; label: string; icon: IconName }[] = [
  { to: "ai", label: "Ember", icon: "ai" },
  { to: "knowledge", label: "Knowledge", icon: "book" },
  { to: "customers", label: "Customers", icon: "customers" },
  { to: "analytics", label: "Analytics", icon: "analytics" },
  { to: "automations", label: "Automations", icon: "bolt" },
  { to: "integrations", label: "Integrations", icon: "plug" },
  { to: "settings", label: "Settings", icon: "settings" },
];

export function useTheme(): [boolean, (dark: boolean) => void] {
  const [dark, setDark] = useState(() => localStorage.getItem("ap-theme") === "dark");
  useEffect(() => {
    localStorage.setItem("ap-theme", dark ? "dark" : "light");
    document.getElementById("ap-root")?.classList.toggle("dark", dark);
    document.body.style.background = dark ? "#12110F" : "#FBF9F4";
  }, [dark]);
  return [dark, setDark];
}

export function AppShell({ children }: { children: ReactNode }) {
  const { workspace, me, isAdmin } = useWorkspace();
  const wid = workspace.id;
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [dark, setDark] = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const realtime = useRealtime();
  const toast = useToast();
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  const counts = useQuery({ queryKey: countsKey(wid), queryFn: () => api.get<ConversationCounts>(ws(wid, "conversations/counts")), refetchInterval: 60_000 });
  const views = useQuery({ queryKey: ["views", wid], queryFn: () => api.get<{ items: SavedView[] }>(ws(wid, "views")) });
  const teams = useQuery({ queryKey: ["teams", wid], queryFn: () => api.get<{ items: Team[] }>(ws(wid, "teams")) });
  const usage = useQuery({ queryKey: ["usage", wid], queryFn: () => api.get<Usage>(ws(wid, "usage")), staleTime: 60_000 });
  const analytics = useQuery({ queryKey: ["sidebar-analytics", wid], queryFn: () => api.get<AnalyticsSummary>(ws(wid, "analytics?range=30d")), staleTime: 60_000 });

  useRealtimeEvent((e) => {
    if (e.type === "conversation.upserted") qc.invalidateQueries({ queryKey: countsKey(wid) });
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const inInbox = location.pathname.startsWith(`/app/${wid}/inbox`);
  const folder = params.get("folder") ?? (params.get("view") || params.get("team") ? null : "all");
  const c = counts.data;

  return (
    <>
      <div className="mobile-nav"><Button variant="ghost" icon={mobileOpen ? "x" : "menu"} aria-label={mobileOpen ? "Close navigation" : "Open navigation"} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)} /><Logo wordmark size={22} /><Button variant="ghost" icon="search" aria-label="Search Hearthdesk" onClick={() => setPaletteOpen(true)} /></div>
      {mobileOpen && <button type="button" className="side-dismiss" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
      <div className="shell">
        <nav className={`side${mobileOpen ? " mobile-open" : ""}`} aria-label="Main">
          <Menu
            align="left"
            trigger={(p) => (
              <button type="button" className="ws" {...p}>
                <Logo size={28} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: "block", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{workspace.name}</span>
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    {workspace.plan[0]!.toUpperCase() + workspace.plan.slice(1)} plan{usage.data ? ` · ${usage.data.seatsUsed} ${usage.data.seatsUsed === 1 ? "seat" : "seats"}` : ""}
                  </span>
                </span>
                <Icon name="updown" size="sm" style={{ marginLeft: "auto", color: "var(--tx3)" }} />
              </button>
            )}
            items={[
              { header: "Workspaces" },
              ...me.workspaces.map((w) => ({ label: w.name, icon: (w.id === wid ? "check" : "inbox") as IconName, onSelect: () => navigate(`/app/${w.id}/inbox`) })),
              { label: "Create workspace", icon: "plus", onSelect: () => navigate("/app/new") },
            ]}
          />
          <button type="button" className="srch" onClick={() => setPaletteOpen(true)}>
            <Icon name="search" size="sm" />
            <span>Search or jump to…</span>
            <Kbd>⌘K</Kbd>
          </button>

          <div className="sec">Inbox</div>
          {INBOX_FOLDERS.map((f) => (
            <NavLink key={f.id} to={`/app/${wid}/inbox?folder=${f.id}`} className={() => `nav${inInbox && folder === f.id ? " on" : ""}`}>
              <Icon name={f.icon} />
              <span>{f.label}</span>
              {c && <span className={`ct${f.id === "ai" ? " ai-ct" : ""}`}>{c[f.count]}</span>}
            </NavLink>
          ))}
          {counts.isError && <div className="sidebar-error" role="status">Counts unavailable <button className="link" onClick={() => void counts.refetch()}>Retry</button></div>}

          <div className="sec">Views{isAdmin && <Button variant="ghost" size="sm" icon="plus" aria-label="Manage saved views" onClick={() => navigate(`/app/${wid}/settings/views`)} />}</div>
          {views.data?.items.map((v) => (
            <NavLink key={v.id} to={`/app/${wid}/inbox?view=${v.id}`} className={() => `nav${inInbox && params.get("view") === v.id ? " on" : ""}`}>
              <span className="dotc" style={{ background: v.color }} />
              <span>{v.name}</span>
              {c && <span className="ct">{c.views[v.id] ?? 0}</span>}
            </NavLink>
          ))}
          {views.isPending && <div className="sidebar-empty">Loading views…</div>}
          {views.isError && <div className="sidebar-error">Views unavailable <button className="link" onClick={() => void views.refetch()}>Retry</button></div>}
          {views.data?.items.length === 0 && <div className="sidebar-empty">No saved views yet</div>}

          <div className="sec">Teams{isAdmin && <Button variant="ghost" size="sm" icon="plus" aria-label="Manage teams" onClick={() => navigate(`/app/${wid}/settings/team`)} />}</div>
          {teams.data?.items.map((t) => (
            <NavLink key={t.id} to={`/app/${wid}/inbox?team=${t.id}`} className={() => `nav${inInbox && params.get("team") === t.id ? " on" : ""}`}>
              <Icon name="users" />
              <span>{t.name}</span>
              {c && <span className="ct">{c.teams[t.id] ?? 0}</span>}
            </NavLink>
          ))}
          {teams.isPending && <div className="sidebar-empty">Loading teams…</div>}
          {teams.isError && <div className="sidebar-error">Teams unavailable <button className="link" onClick={() => void teams.refetch()}>Retry</button></div>}
          {teams.data?.items.length === 0 && <div className="sidebar-empty">No teams yet</div>}

          <div className="sec">Workspace</div>
          {WORKSPACE_NAV.map((n) => (
            <NavLink key={n.to} to={`/app/${wid}/${n.to}`} className={({ isActive }) => `nav${isActive ? " on" : ""}`}>
              <Icon name={n.icon} />
              <span>{n.label}</span>
              {n.to === "ai" && analytics.data && <span style={{ marginLeft: "auto" }}><Badge tone="ai" title="Conversations resolved by Ember in the last 30 days">{Math.round(analytics.data.aiResolutionRate * 100)}%</Badge></span>}
            </NavLink>
          ))}

          <div className="side-footer">
            <div className="sfoot">
              <Avatar name={me.user.name} color={me.user.avatarColor} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me.user.name}</span>
                <span className="connection-state" data-status={realtime.status} title={me.user.email}><i aria-hidden="true" />{realtime.status === "open" ? "Connected" : realtime.status === "connecting" ? "Connecting…" : "Reconnecting…"}</span>
              </span>
              <Tooltip content={dark ? "Switch to light theme" : "Switch to dark theme"}><Button variant="ghost" size="sm" icon={dark ? "sun" : "moon"} aria-label={dark ? "Switch to light theme" : "Switch to dark theme"} onClick={() => setDark(!dark)} /></Tooltip>
              <Menu trigger={(props) => <Button variant="ghost" size="sm" icon="more" aria-label="Your account" {...props} />} items={[
                { header: me.user.email },
                { label: "Settings", icon: "settings", onSelect: () => navigate(`/app/${wid}/settings`) },
                { label: "Sign out", icon: "logout", onSelect: () => {
                  void api.post("auth/logout").then(() => { qc.setQueryData(meKey, null); navigate("/login"); }).catch((error: unknown) => { toast(error instanceof Error ? error.message : "Could not sign out. Please try again.", { tone: "bad" }); });
                } },
              ]} />
            </div>
          </div>
        </nav>
        {children}
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}
